import React, { useEffect, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import { Area, Point } from 'react-easy-crop/types';
import { Trans, useTranslation } from 'react-i18next';
import ReactModal from 'react-modal';

import { useOrganization } from '../OrganizationContext';
import Account from '../api/Account';
import { FailedToUploadImageError, ImageFormatError, ImageSizeError, RemoveImageError, initRemoveImage, initUploadImage } from '../api/Image';
import ButtonSecondary, { SecondaryButtonType, SecondaryButtonVariant } from '../components/ButtonSecondary';
import ContentContainer from '../components/ContentContainer';
import ErrorMessage from '../components/ErrorMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import Modal from '../components/Modal';
import { useDocumentTitle } from '../hooks/UseDocumentTitle';
import { FetchImageStatus, fetchImage } from '../image';
import { useURLHelper } from '../urlhelper';
import './ChangeImagePage.css'


enum ImageInModal {
  Current,
  New,
}
enum ModalContent {
  Image,
  RemoveImage,
  CropImage,
}
interface ImageModalProps {
  show: boolean,
  handleClose: React.MouseEventHandler<HTMLButtonElement>,
  content: ModalContent,
  image: ImageInModal,
}
interface Props {
  account: Account;
}
function ChangeImagePage({account}:Props): React.ReactElement {
  const {t} = useTranslation<string>();
  const org = useOrganization();
  const urlHelper = useURLHelper();
  const { image, status } = fetchImage({org, account})
  const sessionStorageKey = `${org.id}-account-image`;
  const [newImage, setNewImage] = useState<Blob>();
  const [newImageURL, setNewImageURL] = useState<string | undefined>(undefined);
  const [newImageName, setNewImageName] = useState<string>();
  const [currentImage, setCurrentImage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showError, setShowError] = useState<boolean>(false);
  const [isUploadingImage, setIsUploadingImage] = useState<boolean>(false);
  const [isRemovingImage, setIsRemovingImage] = useState<boolean>(false);
  const [showModal, setShowModal] = useState(false);
  const [contentInModal, setContentInModal] = useState<ModalContent>(ModalContent.Image);
  const [imageInModal, setImageInModal] = useState<ImageInModal>(ImageInModal.Current);
  const orgname = org.name.localize();

  useDocumentTitle({title: 'Change profile image'});

  async function uploadNewImage() {
    setIsUploadingImage(true);
    try {
      if (newImage) {
        await initUploadImage({
          orgID: org.backendId,
          accountID: account.id,
          image: newImage,
        });
        // Convert new Image to base64 and display as current Image
        const reader = new FileReader();
        reader.readAsDataURL(newImage);
        reader.onloadend = () => {
          if(reader.result) {
            const base64data = reader.result.toString().split(',');
            setCurrentImage(base64data[1]);
            window.sessionStorage.setItem(sessionStorageKey, base64data[1]);
          }        
        }
        setNewImage(undefined)
      }
    } catch (e) {
      if (e instanceof ImageSizeError) {
        setErrorMessage('The file size of the image you selected is too large (Max 5MB). Compress the image or choose a smaller one.');
      } else if (e instanceof ImageFormatError) {
        setErrorMessage('Image file error. Try again or with a different image. Contact helpdesk if the problem persists.');
      } else if (e instanceof FailedToUploadImageError) {
        setErrorMessage('Error uploading image to our systems. Please try again, or contact helpdesk if the problem persists.');
      } else {
        urlHelper.genericError((e as Error).message);
        return;
      }
      setShowError(true);
    }
    setIsUploadingImage(false);
    if (newImageURL) {
      URL.revokeObjectURL(newImageURL);
    }
  }

  async function removeUploadedImage() {
    setIsRemovingImage(true);
    setShowModal(false);
    try {
      await initRemoveImage(org.backendId,account.id);
      setCurrentImage('');
      window.sessionStorage.removeItem(sessionStorageKey);
    } catch (e) {
      if (e instanceof RemoveImageError) {
        setErrorMessage('Error removing image. Please try again, or contact helpdesk if the problem persists.');
      } else {
        urlHelper.genericError((e as Error).message);
        return;
      }
      setShowError(true);
    }
    setIsRemovingImage(false);
  }

  function ChooseImageButton(): React.ReactElement {
    const fileRef = useRef<HTMLInputElement>(null);
    function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
      if (event.target.files) {
        const file = event.target.files[0];
        // Filetype has to be jpeg or png
        if (file.type != 'image/jpeg' && file.type != 'image/png') {
          setErrorMessage('Filetype is not valid. Image must be a jpeg or png.');
          setShowError(true);
        // Filesize must be under 5MB.
        } else if (file.size > (5 * 1024 * 1024)) {
          setErrorMessage('The file size of the image you selected is too large (Max 5MB). Compress the image or choose a smaller one.');
          setShowError(true);
        } else {
          setNewImageName(event.target.files[0].name);
          setNewImageURL(URL.createObjectURL(event.target.files[0]));
          setShowError(false);
          setImageInModal(ImageInModal.New);
          setContentInModal(ModalContent.CropImage);
          openModal();
        }
      }
    }
    return(
      <div className='ChangeImagePage-ChooseImageButton'>
        <ButtonSecondary 
          fullWidth 
          type={SecondaryButtonType.button} 
          onClick={() => fileRef.current && fileRef.current.click()} 
          ariaLabel={t('Upload an image from your device')}>
          {t('Choose image')}
        </ButtonSecondary>
        <input hidden ref={fileRef} onChange={handleChange} type='file' accept='image/jpeg, image/png' />
      </div>
    )
  }

  const openModal = () => {
    setShowModal(true);
  }
  const hideModal = () => {
    setShowModal(false);
    if (newImageURL) {
      URL.revokeObjectURL(newImageURL);
    }
  }

  // Set the current image if it exists.
  useEffect(() => {
    if(image) {
      setCurrentImage(image);
    }
  }, [image])

  // Release the object URL when the component unmounts or when the new image changes
  useEffect(() => {
    return () => {
      if (newImageURL) {
        URL.revokeObjectURL(newImageURL);
      }
    };
  }, [newImageURL]);

  function ChangeImagePageModal(props: ImageModalProps): React.ReactElement {
    const [crop, setCrop] = useState<Point>({x: 0, y: 0});
    const [zoom, setZoom] = useState<number>(1);
    const [rotation, setRotation] = useState<number>(0);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area>();

    // Set the cropped image as the new image to be uploaded
    const setCroppedImage = (url:string) => {
      setErrorMessage('Image file error. Try again or with a different image. Contact helpdesk if the problem persists.');
      const image = new Image();
      image.src = url;

      image.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (ctx) {
          // First calculate the rotated dimensions
          const radianAngle = (rotation * Math.PI) / 180;
          const rotatedWidth = Math.abs(Math.cos(radianAngle) * image.width) + Math.abs(Math.sin(radianAngle) * image.height);
          const rotatedHeight = Math.abs(Math.sin(radianAngle) * image.width) + Math.abs(Math.cos(radianAngle) * image.height);
          canvas.width = rotatedWidth;
          canvas.height = rotatedHeight;
          // Draw canvas based on the rotation
          ctx.translate(rotatedWidth / 2, rotatedHeight / 2);
          ctx.rotate(radianAngle);
          ctx.translate(-rotatedWidth / 2, -rotatedHeight / 2);
          ctx.drawImage(image, 0, 0);
          // Then draw the cropped area on the canvas
          const croppedCanvas = document.createElement('canvas');
          const croppedCtx = croppedCanvas.getContext('2d');
          if (croppedCtx && croppedAreaPixels) {
            croppedCanvas.width = croppedAreaPixels.width;
            croppedCanvas.height = croppedAreaPixels.height;
            croppedCtx.drawImage(canvas, croppedAreaPixels.x, croppedAreaPixels.y, croppedAreaPixels.width, croppedAreaPixels.height, 0, 0, croppedAreaPixels.width, croppedAreaPixels.height);
            // Convert the canvas to a blob
            croppedCanvas.toBlob((blob) => {
              if (blob) {
                setNewImage(blob);
                hideModal();
              } else {
                setShowError(true);
              }
            }, 'image/jpeg');
          } else {
            setShowError(true);
          }
        } else {
          setShowError(true);
        }
      }
      image.onerror = () => {
        setShowError(true);
      }
    }
    // Update the cropped area when the cropper is moved
    const onCropComplete = (croppedArea: Area, croppedAreaPixels: Area) => {
      if (croppedAreaPixels) {
        setCroppedAreaPixels(croppedAreaPixels);
      }
    };
    // Display the modal with the current/new image, or the remove image confirmation.
    if (props.content === ModalContent.Image || props.content === ModalContent.RemoveImage) {
      return (
        <Modal
          id='modal'
          isOpen={props.show}
          onRequestClose={props.handleClose}
          title={props.content===ModalContent.RemoveImage ? t('Remove current image') : ''}
        >
          { props.content === ModalContent.Image &&
          // Open the Modal with the image, current or new depending on the state
            <div className='ChangeImagePage-modal-image'>
              { imageInModal===ImageInModal.Current && currentImage && <img src={`data:image;base64, ${currentImage}`} /> }
              { imageInModal===ImageInModal.New && newImage && <img src={URL.createObjectURL(newImage)} alt={t('Profile picture')} /> }
            </div>
          }
          { props.content === ModalContent.RemoveImage &&
          // Open the Modal with the remove image confirmation
            <div>
              <p>{t('Are you sure you want to remove your current image?')}</p>
              <p>{t('This will permanently remove your current photo from our system.')}</p>
              <div className='ChangeImagePage-modal-button-container'>
                <ButtonSecondary onClick={() => removeUploadedImage()} type={SecondaryButtonType.button} disabled={isRemovingImage} variant={SecondaryButtonVariant.contained}>{t('Remove')}</ButtonSecondary>
                <ButtonSecondary onClick={() => setShowModal(false)} type={SecondaryButtonType.button} variant={SecondaryButtonVariant.outlinedGhost}>{t('Cancel')}</ButtonSecondary>
              </div>
            </div>
          }
        </Modal>
      )} else {
      // Display the modal with the cropper functionality
      return (
        <ReactModal isOpen={props.show} onRequestClose={props.handleClose} className='ChangeImagePage-modal-crop-main' overlayClassName='ChangeImagePage-modal-crop-background' aria={{labelledby: 'modal-heading', describedby: 'modal-details'}}>
          <div className='ChangeImagePage-modal-crop-container'>
            <Cropper image={newImageURL} zoom={zoom} crop={crop} rotation={rotation} aspect={4/5} onCropChange={setCrop} onZoomChange={setZoom} onRotationChange={setRotation} onCropComplete={onCropComplete}/>
          </div>
          <div className='ChangeImagePage-modal-crop-controls'>
            <div>
              <label htmlFor='zoom'>{t('Zoom')}</label>
              <input id='zoom' type='range' min={1} max={3} step={0.1} value={zoom} onChange={(e) => setZoom(parseFloat(e.target.value))}/>
            </div>
            <div>
              <label htmlFor='rotation'>{t('Rotate')}</label>
              <input id='rotation' type='range' min={0} max={360} step={1} value={rotation} onChange={(e) => setRotation(parseInt(e.target.value))} list='markers'/>
            </div>
            <datalist id='markers'>
              <option value='90'/>
              <option value='180' />
              <option value='270' />
            </datalist>
          </div>
          <div className='ChangeImagePage-modal-button-container'>
            {newImageURL && <ButtonSecondary onClick={() => setCroppedImage(newImageURL)} type={SecondaryButtonType.button} variant={SecondaryButtonVariant.contained}>{t('Select')}</ButtonSecondary>}
            <ButtonSecondary onClick={() => setShowModal(false)} type={SecondaryButtonType.button} variant={SecondaryButtonVariant.outlinedGhost}>{t('Cancel')}</ButtonSecondary>
          </div>
        </ReactModal>
      )}
  }

  return (
    <ContentContainer
      header={t('Change image')}
      title={t('Change image for your account')}
      footer={
        <div className='ChangeImagePage-footer'>
          <ButtonSecondary 
            linkTo={`/${org.id}/profile`} 
            type={SecondaryButtonType.link} 
            variant={SecondaryButtonVariant.contained}>
            {t('Back')}
          </ButtonSecondary>
          {newImage && 
            <ButtonSecondary 
              onClick={() => uploadNewImage()} 
              type={SecondaryButtonType.button} 
              variant={SecondaryButtonVariant.contained} 
              disabled={isUploadingImage} 
              ariaLabel={t('Upload image {{newImageName}}', newImageName)}>
              { isUploadingImage ? <><LoadingSpinner ariaLabel={t('')}/>{t('Uploading')}</> : t('Upload')}
            </ButtonSecondary>
          }
        </div>
      }
    >
      <p> { org.imageRequirementsLink ? 
        <Trans>Make sure you choose an image that <a target='_blank' rel='noopener noreferrer' href={org.imageRequirementsLink.localize()}>meets the requirements</a>.</Trans>
        :
        <Trans>Make sure you choose an image that meets the requirements.</Trans>}
      </p>
      <p> { org.imageUsageLink &&  <Trans>Read about how {{orgname}} <a target='_blank' rel='noopener noreferrer' href={org.imageUsageLink.localize()}>uses your image</a>.</Trans> }</p>
      <p>{t('Remember to press Upload for the image to be updated in our systems.')}</p>
      <div aria-live='polite' role='status'>
        <ErrorMessage  showError={showError} message={errorMessage} />
      </div>
      {
        // If the image is loading, show a loading spinner
        status===FetchImageStatus.Loading ?
          <div className='ChangeImagePage-image-div'>
            <label>{t('Loading image')}</label>
            <div className='ChangeImagePage-image-loading-background'><div className='ChangeImagePage-image-loading'></div></div>
          </div>
          :
          // Display the current image if it exists and the user has not selected a new image
          currentImage && !newImage ?
            <div className='ChangeImagePage-image-div'>
              <label htmlFor='currentImage'>{t('Your image')}</label>
              <img onClick={() => {openModal(); setImageInModal(ImageInModal.Current); setContentInModal(ModalContent.Image)}} alt={t('Current uploaded image')} id='currentImage' className='ChangeImagePage-image' src={`data:image;base64, ${currentImage}`}/>
            </div>
            :
            // Display the new cropped image the user has selected
            newImage ?
              <div className='ChangeImagePage-image-div'>
                <label htmlFor='newImage'>{t('New image')}</label>
                <img onClick={() => {openModal(); setImageInModal(ImageInModal.New); setContentInModal(ModalContent.Image)}} alt={t('New image to upload')} id='newImage' className='ChangeImagePage-image' src={URL.createObjectURL(newImage)} />
              </div>
              :
              <></>
      }
      <div className='ChangeImagePage-button-section'>
        <ChooseImageButton />
        {currentImage && org.profilePageOptions.removeImage() &&
        <ButtonSecondary fullWidth type={SecondaryButtonType.button} onClick={() => {setContentInModal(ModalContent.RemoveImage);openModal()}} disabled={isRemovingImage}>
          { isRemovingImage ? <><LoadingSpinner ariaLabel={t('Removing current image')}/>{t('Removing current image')} </> : t('Remove current image')} 
        </ButtonSecondary>}
      </div>
      <ChangeImagePageModal show={showModal} handleClose={hideModal} content={contentInModal} image={imageInModal} />
    </ContentContainer>
  )
}

export default ChangeImagePage;